import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { carModelsAPI, carBrandsAPI } from "../../services/api";
import { CarModel, CarBrand, CarType } from "../../types";

const carTypes: CarType[] = [
  "Hatchback",
  "SUV",
  "Sedan",
  "MUV",
  "Minivan",
  "Convertible",
  "Other",
];

const CarModels: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<CarModel | null>(null);
  const [models, setModels] = useState<CarModel[]>([]);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [form, setForm] = useState({
    brand_id: 0,
    name: "",
    car_type: "Sedan" as CarType,
    status: true,
  });

  useEffect(() => {
    fetchBrands();
    fetchModels();
  }, []);

  const fetchBrands = async () => {
    const data = await carBrandsAPI.getAll();

    const formatted = data.map((b: any) => ({
      brand_id: Number(b.brand_id),
      name: b.name,
      logo_url: b.logo_url || b.logo || "",
      status: Boolean(b.status),
      created_at: b.created_at || new Date().toISOString(),
      updated_at: b.updated_at || new Date().toISOString(),
    }));

    setBrands(formatted);
  };

  const fetchModels = async () => {
    const resp = await carModelsAPI.getAll();
    setModels(resp?.data || []);
  };

  const openForm = (model?: CarModel) => {
    if (model) {
      setEditing(model);
      setForm({
        brand_id: model.brand_id,
        name: model.name,
        car_type: model.car_type,
        status: model.status,
      });
    } else {
      setEditing(null);
      setForm({
        brand_id: brands[0]?.brand_id || 0,
        name: "",
        car_type: "Sedan",
        status: true,
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) await carModelsAPI.update(editing.model_id, form);
    else await carModelsAPI.create(form);
    fetchModels();
    setOpenDialog(false);
  };

  const deleteModel = async (id: number) => {
    if (!window.confirm("Delete this model?")) return;
    await carModelsAPI.delete(id);
    fetchModels();
  };

  const toggleStatus = async (model: CarModel) => {
    await carModelsAPI.update(model.model_id, {
      ...model,
      status: !model.status,
    });
    fetchModels();
  };

  const getBrandName = (brand_id: number) =>
    brands.find((b) => b.brand_id === brand_id)?.name || "Unknown";

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Car Models
      </Typography>

      <Button
        variant="contained"
        onClick={() => openForm()}
        startIcon={<Add />}
        disabled={brands.length === 0}
      >
        Add Model
      </Button>

      {brands.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please add car brands first.
        </Alert>
      )}

      <Paper sx={{ mt: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell>Brand</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {models.map((m) => (
                <TableRow key={m.model_id}>
                  <TableCell>{getBrandName(m.brand_id)}</TableCell>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.car_type}</TableCell>
                  <TableCell>
                    <Chip
                      label={m.status ? "Active" : "Inactive"}
                      color={m.status ? "success" : "default"}
                      onClick={() => toggleStatus(m)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => openForm(m)}>
                      <Edit />
                    </IconButton>
                    <IconButton color="error" onClick={() => deleteModel(m.model_id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {models.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No models found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>{editing ? "Edit Model" : "Add Model"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Brand</InputLabel>
              <Select
                name="brand_id"
                value={form.brand_id}
                label="Brand"
                onChange={(e) =>
                  setForm({ ...form, brand_id: Number(e.target.value) })
                }
              >
                {brands.map((b) => (
                  <MenuItem key={b.brand_id} value={b.brand_id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Model Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Car Type</InputLabel>
              <Select
                value={form.car_type}
                label="Car Type"
                onChange={(e) =>
                  setForm({ ...form, car_type: e.target.value as CarType })
                }
              >
                {carTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" type="submit">
              {editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CarModels;
